// Interactive geofence-review map for the Radius Report. Plots each clock-in/out
// event as a colour-coded pin (green = inside the geofence, red = outside) and, when a
// row is selected, draws that shift's scheduled-address geofence circle and flies to it.
// Prefers the Google Maps engine (browser key from /api/maps-config) and falls back to
// Leaflet so the map still works when no key is configured — same strategy as LiveTrackingMap.
import React, { useEffect, useRef, useState, useCallback } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { MapPin, Maximize2 } from 'lucide-react';
import api from '../../utils/api';
import { loadGoogleMaps } from '../../utils/googleMaps';

const GREEN = '#16a34a';
const RED   = '#dc2626';
const BLUE  = '#4f46e5';

const num = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : null; };

// Flatten report rows into individual map events (a shift can contribute a clock-in and a clock-out pin).
function toEvents(rows) {
  const out = [];
  for (const r of rows || []) {
    const ciLat = num(r.clock_in_lat),  ciLng = num(r.clock_in_lng);
    const coLat = num(r.clock_out_lat), coLng = num(r.clock_out_lng);
    if (ciLat != null && ciLng != null) out.push({
      id: r.shift_id, kind: 'in', lat: ciLat, lng: ciLng,
      outside: !!r.clock_in_outside, dist: r.clock_in_distance_m, staff: r.staff_name,
      address: r.scheduled_address, schedLat: num(r.scheduled_lat), schedLng: num(r.scheduled_lng),
    });
    if (coLat != null && coLng != null) out.push({
      id: r.shift_id, kind: 'out', lat: coLat, lng: coLng,
      outside: !!r.clock_out_outside, dist: r.clock_out_distance_m, staff: r.staff_name,
      address: r.scheduled_address, schedLat: num(r.scheduled_lat), schedLng: num(r.scheduled_lng),
    });
  }
  return out;
}

function popupHtml(e, radius) {
  const col = e.outside ? RED : GREEN;
  return `<div style="font-family:system-ui;padding:4px 2px;min-width:170px">
    <div style="font-weight:700;font-size:13px;color:#1e293b">${e.staff || 'Staff'}</div>
    <div style="font-size:11px;color:${col};font-weight:700;margin-top:2px;text-transform:uppercase;letter-spacing:.03em">
      Clock-${e.kind} · ${e.outside ? 'Outside' : 'Inside'} (${e.dist ?? '?'}m / ${radius}m)</div>
    ${e.address ? `<div style="font-size:11px;color:#64748b;margin-top:4px">&#128205; ${e.address}</div>` : ''}
  </div>`;
}

export default function RadiusMap({ rows = [], radius = 400, selectedId = null, onSelect }) {
  const mapEl   = useRef(null);
  const mapObj  = useRef(null);
  const gRef    = useRef(null);          // google.maps namespace once loaded
  const engine  = useRef('leaflet');
  const infoRef = useRef(null);          // shared Google InfoWindow
  const layers  = useRef([]);            // all event markers (for clearing)
  const geoRef  = useRef(null);          // selected geofence circle + center marker
  const eventsRef = useRef([]);
  const renderRef = useRef(() => {});
  const focusRef  = useRef(() => {});
  const [mapsKey, setMapsKey] = useState(null); // null=loading, ''=leaflet, key=google

  useEffect(() => { api.get('/maps-config').then(r => setMapsKey(r.data?.googleMapsKey || '')).catch(() => setMapsKey('')); }, []);

  const clearLayers = useCallback(() => {
    if (engine.current === 'google') { layers.current.forEach(m => m.setMap(null)); }
    else { layers.current.forEach(m => m.remove()); }
    layers.current = [];
  }, []);

  const fitAll = useCallback(() => {
    const evs = eventsRef.current;
    if (!mapObj.current || !evs.length) return;
    if (engine.current === 'google') {
      const b = new gRef.current.LatLngBounds();
      evs.forEach(e => b.extend({ lat: e.lat, lng: e.lng }));
      mapObj.current.fitBounds(b, 60);
      if (evs.length === 1) mapObj.current.setZoom(15);
    } else {
      mapObj.current.fitBounds(evs.map(e => [e.lat, e.lng]), { padding: [50, 50], maxZoom: 15 });
    }
  }, []);

  // Draw every event pin. Clock-in = solid dot, clock-out = ringed dot; colour by inside/outside.
  const renderEvents = useCallback(() => {
    if (!mapObj.current) return;
    clearLayers();
    const evs = eventsRef.current;
    for (const e of evs) {
      const col = e.outside ? RED : GREEN;
      if (engine.current === 'google') {
        const maps = gRef.current;
        const m = new maps.Marker({
          position: { lat: e.lat, lng: e.lng }, map: mapObj.current,
          icon: {
            path: maps.SymbolPath.CIRCLE, scale: e.kind === 'in' ? 8 : 6,
            fillColor: col, fillOpacity: e.kind === 'in' ? 1 : 0.55,
            strokeColor: '#fff', strokeWeight: 2,
          },
          title: `${e.staff || 'Staff'} · clock-${e.kind}`,
        });
        m.addListener('click', () => { onSelect?.(e.id); infoRef.current.setContent(popupHtml(e, radius)); infoRef.current.open(mapObj.current, m); });
        layers.current.push(m);
      } else {
        const m = L.circleMarker([e.lat, e.lng], {
          radius: e.kind === 'in' ? 8 : 6, color: '#fff', weight: 2,
          fillColor: col, fillOpacity: e.kind === 'in' ? 1 : 0.55,
        }).addTo(mapObj.current).bindPopup(popupHtml(e, radius), { maxWidth: 220 });
        m.on('click', () => onSelect?.(e.id));
        layers.current.push(m);
      }
    }
    fitAll();
  }, [clearLayers, fitAll, onSelect, radius]);
  renderRef.current = renderEvents;

  // Highlight the selected shift: geofence circle at the scheduled address + fly to it.
  const focusSelected = useCallback(() => {
    if (!mapObj.current || engine.current === 'google' && !gRef.current) return;
    // clear previous geofence overlay
    if (geoRef.current) {
      if (engine.current === 'google') { geoRef.current.circle?.setMap(null); geoRef.current.center?.setMap(null); }
      else { geoRef.current.circle?.remove(); geoRef.current.center?.remove(); }
      geoRef.current = null;
    }
    if (!selectedId) return;
    const ev = eventsRef.current.find(e => e.id === selectedId);
    if (!ev) return;
    const center = (ev.schedLat != null && ev.schedLng != null) ? { lat: ev.schedLat, lng: ev.schedLng } : { lat: ev.lat, lng: ev.lng };

    if (engine.current === 'google') {
      const maps = gRef.current;
      const circle = new maps.Circle({
        map: mapObj.current, center, radius,
        strokeColor: BLUE, strokeOpacity: 0.8, strokeWeight: 2, fillColor: BLUE, fillOpacity: 0.08,
      });
      const cm = new maps.Marker({
        position: center, map: mapObj.current,
        icon: { path: maps.SymbolPath.CIRCLE, scale: 5, fillColor: BLUE, fillOpacity: 1, strokeColor: '#fff', strokeWeight: 2 },
        title: ev.address || 'Scheduled location',
      });
      geoRef.current = { circle, center: cm };
      mapObj.current.panTo(center);
      mapObj.current.fitBounds(circle.getBounds(), 80);
    } else {
      const circle = L.circle([center.lat, center.lng], {
        radius, color: BLUE, weight: 2, opacity: 0.8, fillColor: BLUE, fillOpacity: 0.08,
      }).addTo(mapObj.current);
      const cm = L.circleMarker([center.lat, center.lng], {
        radius: 5, color: '#fff', weight: 2, fillColor: BLUE, fillOpacity: 1,
      }).addTo(mapObj.current);
      geoRef.current = { circle, center: cm };
      mapObj.current.fitBounds(circle.getBounds(), { padding: [60, 60] });
    }
  }, [selectedId, radius]);
  focusRef.current = focusSelected;

  // Init map once we know whether a Google key exists.
  useEffect(() => {
    if (mapsKey === null || !mapEl.current) return;
    let cancelled = false;

    function initLeaflet() {
      engine.current = 'leaflet';
      const map = L.map(mapEl.current, { zoomControl: true, attributionControl: false }).setView([-33.8688, 151.2093], 11);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 19, attribution: '&copy; OpenStreetMap contributors', subdomains: 'abc' }).addTo(map);
      mapObj.current = map;
      renderRef.current();
      focusRef.current();
    }

    if (mapsKey) {
      loadGoogleMaps(mapsKey).then((maps) => {
        if (cancelled || !mapEl.current) return;
        gRef.current = maps;
        engine.current = 'google';
        mapObj.current = new maps.Map(mapEl.current, {
          center: { lat: -33.8688, lng: 151.2093 }, zoom: 11,
          mapTypeControl: true, streetViewControl: false, fullscreenControl: false,
        });
        infoRef.current = new maps.InfoWindow();
        renderRef.current();
        focusRef.current();
      }).catch(() => { if (!cancelled) initLeaflet(); });
    } else {
      initLeaflet();
    }

    return () => {
      cancelled = true;
      if (engine.current === 'leaflet' && mapObj.current) mapObj.current.remove();
      mapObj.current = null; layers.current = []; geoRef.current = null;
    };
  }, [mapsKey]);

  // Re-render pins whenever the rows change.
  useEffect(() => { eventsRef.current = toEvents(rows); if (mapObj.current) renderRef.current(); }, [rows]);

  // Re-focus whenever the selection changes.
  useEffect(() => { if (mapObj.current) focusRef.current(); }, [selectedId]);

  const events = toEvents(rows);
  const hasCoords = events.length > 0;

  return (
    <div className="relative rounded-xl overflow-hidden border border-slate-200">
      <div ref={mapEl} className="h-[420px] w-full bg-slate-100" style={{ zIndex: 0 }} />

      {!hasCoords && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-slate-50/90 text-center px-6 pointer-events-none">
          <div className="w-11 h-11 rounded-full bg-slate-100 flex items-center justify-center"><MapPin size={20} className="text-slate-400" /></div>
          <p className="text-sm font-semibold text-slate-600">No mapped clock events</p>
          <p className="text-xs text-slate-400">Events appear here once staff clock in/out with location captured.</p>
        </div>
      )}

      {/* Legend + fit-all (kept bottom to avoid the map's top-left native controls) */}
      <div className="absolute bottom-3 left-3 z-[400] flex items-center gap-3 bg-white/95 backdrop-blur border border-slate-200 rounded-lg px-3 py-1.5 shadow-sm text-[11px] font-semibold text-slate-600">
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: GREEN }} />Inside</span>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: RED }} />Outside</span>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full border-2 border-white ring-1 ring-slate-300" style={{ background: BLUE }} />Geofence</span>
      </div>
      {hasCoords && (
        <button onClick={() => fitAll()} title="Fit all events"
          className="absolute bottom-3 right-3 z-[400] flex items-center gap-1.5 bg-white/95 backdrop-blur border border-slate-200 rounded-lg px-2.5 py-1.5 shadow-sm text-[11px] font-semibold text-primary-600 hover:bg-primary-50">
          <Maximize2 size={12} /> Fit all
        </button>
      )}
    </div>
  );
}
